import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Check, Copy, RefreshCw } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  FormModal,
  Input,
  SegmentedControl,
  Tooltip,
} from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { generateSecurePassword } from "@marekl/adminer/util";
import { notifyError, notifySuccess, notifyWarning } from "@marekl/adminer/api";
import type { Role, User } from "../../auth/queries";
import {
  useCreateUser,
  useUpdateUser,
  type UserCreate,
} from "../../lib/queries/users";
import { useSaveCompanyAccess, type Company, type CompanyAccess } from "../../lib/queries/companies";
import { CompanyAccessFields } from "./CompanyAccessFields";

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: "user", label: "Uživatel" },
  { value: "admin", label: "Administrátor" },
];

export function RoleBadge({ role }: { role: Role }) {
  return role === "admin" ? (
    <Badge variant="err">Administrátor</Badge>
  ) : (
    <Badge variant="info">Uživatel</Badge>
  );
}

/**
 * Small copy-to-clipboard affordance for a revealed generated password —
 * same styling and Copied feedback as TokenRevealModal's copy button.
 */
function CopyPasswordButton({ getValue }: { getValue: () => string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    const value = getValue();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      notifyError(err);
    }
  }

  return (
    <Tooltip content="Zkopírovat heslo do schránky">
      <Button
        type="button"
        size="sm"
        variant="primary"
        weight="outline"
        onClick={copy}
        startIcon={
          copied ? (
            <Check size={14} aria-hidden="true" />
          ) : (
            <Copy size={14} aria-hidden="true" />
          )
        }
      >
        {copied ? "Zkopírováno" : "Kopírovat"}
      </Button>
    </Tooltip>
  );
}

function GeneratePasswordButton({ onGenerate }: { onGenerate: () => void }) {
  return (
    <Tooltip content="Vygenerovat bezpečné heslo">
      <Button
        type="button"
        size="sm"
        variant="primary"
        startIcon={<RefreshCw size={14} aria-hidden="true" />}
        onClick={onGenerate}
      >
        Vygenerovat
      </Button>
    </Tooltip>
  );
}

export function CreateUserModal({ companies, onClose }: { companies: Company[]; onClose: () => void }) {
  const createUser = useCreateUser();
  const saveAccess = useSaveCompanyAccess();
  const [access, setAccess] = useState<CompanyAccess>({});
  // Masked while typed by hand; generating reveals the value so it can be
  // copied and handed to the new user.
  const [revealed, setRevealed] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    watch,
    control,
    formState: { errors },
  } = useForm<UserCreate>({
    mode: "onTouched",
    defaultValues: { username: "", password: "", role: "user" },
  });

  async function onSubmit(values: UserCreate) {
    try {
      const user = await createUser.mutateAsync({
        ...values,
        username: values.username.trim(),
      });
      if (values.role !== "admin") {
        // The user exists now: a failed access save must not invite a second
        // "create" (the username is taken) — say so and let them fix it in edit.
        try {
          await saveAccess.mutateAsync({ userId: user.id, before: {}, after: access });
        } catch {
          notifyWarning("Uživatel byl vytvořen, ale přístup k firmám se nepodařilo uložit. Nastavte ho v úpravě uživatele.");
          onClose();
          return;
        }
      }
      notifySuccess("Uživatel byl vytvořen.");
      onClose();
    } catch (err) {
      apiErrorToFormErrors<UserCreate>(err, setError, {
        fallbackMessage: "Vytvoření se nepovedlo. Zkuste to znovu.",
      });
    }
  }

  return (
    <FormModal
      open
      title="Přidat uživatele"
      onClose={onClose}
      busy={createUser.isPending || saveAccess.isPending}
      error={rootErrorMessage(errors)}
      submitLabel="Vytvořit"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Input
        label="Uživatelské jméno"
        required
        autoComplete="off"
        error={errors.username?.message}
        {...register("username", {
          required: "Povinné pole",
          validate: (v) => (v.trim().length > 0 ? true : "Povinné pole"),
        })}
      />
      <Controller
        name="role"
        control={control}
        render={({ field }) => (
          <SegmentedControl
            label="Role"
            hint="Administrátor vidí všechny firmy a spravuje uživatele."
            options={ROLE_OPTIONS}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
      <Input
        label="Heslo"
        required
        type={revealed ? "text" : "password"}
        autoComplete="new-password"
        spellCheck={false}
        error={errors.password?.message}
        {...register("password", {
          required: "Povinné pole",
          minLength: { value: 10, message: "Heslo musí mít alespoň 10 znaků" },
        })}
        suffix={
          <>
            {revealed && (
              <CopyPasswordButton getValue={() => getValues("password")} />
            )}
            <GeneratePasswordButton
              onGenerate={() => {
                setValue("password", generateSecurePassword(), {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                setRevealed(true);
              }}
            />
          </>
        }
      />
      {companies.length > 0 && (
        <CompanyAccessFields companies={companies} value={access} onChange={setAccess} admin={watch("role") === "admin"} />
      )}
    </FormModal>
  );
}

export function EditUserModal({
  user,
  isSelf,
  companies,
  access: accessBefore,
  onClose,
}: {
  user: User;
  isSelf: boolean;
  companies: Company[];
  access: CompanyAccess;
  onClose: () => void;
}) {
  const updateUser = useUpdateUser();
  const saveAccess = useSaveCompanyAccess();
  const [access, setAccess] = useState<CompanyAccess>(accessBefore);
  const [role, setRole] = useState<Role>(user.role);
  const [active, setActive] = useState(user.active);
  const [password, setPassword] = useState("");
  const [pwError, setPwError] = useState("");
  // Masked while typed by hand; generating reveals the value so it can be
  // copied and handed to the user.
  const [revealed, setRevealed] = useState(false);

  async function onSubmit() {
    if (password && password.length < 10) {
      setPwError("Heslo musí mít alespoň 10 znaků");
      return;
    }
    try {
      await updateUser.mutateAsync({
        id: user.id,
        input: {
          role,
          active,
          // Empty = keep the current password.
          ...(password ? { password } : {}),
        },
      });
      // An administrator sees every company; memberships only matter for users.
      if (role !== "admin") await saveAccess.mutateAsync({ userId: user.id, before: accessBefore, after: access });
      notifySuccess("Uživatel byl upraven.");
      onClose();
    } catch {
      // Shown by the FormModal error slot (updateUser suppresses the toast).
    }
  }

  return (
    <FormModal
      open
      title={`Upravit: ${user.username}`}
      onClose={onClose}
      busy={updateUser.isPending || saveAccess.isPending}
      error={updateUser.error ?? saveAccess.error ?? undefined}
      onSubmit={() => void onSubmit()}
    >
      {isSelf && (
        <Alert variant="info">
          Upravujete vlastní účet — roli ani aktivitu si změnit nemůžete.
        </Alert>
      )}
      <SegmentedControl
        label="Role"
        id="edit-user-role"
        options={ROLE_OPTIONS.map((o) => ({ ...o, disabled: isSelf }))}
        value={role}
        onChange={(v: Role) => setRole(v)}
      />
      <Checkbox
        label="Aktivní"
        checked={active}
        disabled={isSelf}
        onChange={(e) => setActive(e.target.checked)}
      />
      <Input
        id="edit-user-password"
        label="Nové heslo"
        type={revealed ? "text" : "password"}
        value={password}
        autoComplete="new-password"
        spellCheck={false}
        placeholder="ponechte prázdné, heslo se nezmění"
        hint="Vyplňte jen pro nastavení nového hesla."
        error={pwError}
        onChange={(e) => {
          setPassword(e.target.value);
          setPwError("");
        }}
        suffix={
          <>
            {revealed && <CopyPasswordButton getValue={() => password} />}
            <GeneratePasswordButton
              onGenerate={() => {
                setPassword(generateSecurePassword());
                setPwError("");
                setRevealed(true);
              }}
            />
          </>
        }
      />
      {companies.length > 0 && (
        <CompanyAccessFields companies={companies} value={access} onChange={setAccess} admin={role === "admin"} />
      )}
    </FormModal>
  );
}
